import { ScaleTime, Selection, EnterElement } from "d3"
import { ModalityGraphGroupReactCallbacks } from "../../../../Types/ReactCallbacks"
import { TimeSeriesPageManager } from "../../../../Data/TimeSeriesPageManager"
import { PageRectangle, RenderStrategy, TraceConfig } from "../../../../Types/Trace"
import { ModalityPage } from "../../../../Data/ModalityPage"
import { D3OneToOneRenderable } from "../../../D3/D3OneToOneRenderable"
import { LineRenderStrategy } from "./D3LineRenderStrategy"
import { HeatmapRenderStrategy } from "./D3HeatmapRenderStrategy"
import { SpectrogramRenderStrategy } from "./D3SpectrogramRenderStrategy"
import { TraceRenderStrategy } from "./RenderStrategy"
import { DeltaRenderStrategy } from "./D3DeltaRenderStrategy"
import { DataSource } from "../../../../Types/DataSource"
import { LineStepRenderStrategy } from "./D3LineStepRenderStrategy"
import { InfusionMedicationRenderStrategy } from "./D3InfusionMedicationRenderStrategy"
import { IntermittentMedicationRenderStrategy } from "./D3IntermittentMedicationRenderStrategy"
import { PointRenderStrategy } from "./D3PointRenderStrategy"

export class D3Trace extends D3OneToOneRenderable<SVGGElement, SVGForeignObjectElement, TraceConfig, ModalityGraphGroupReactCallbacks> {
	private context?: CanvasRenderingContext2D
	private pageManager: TimeSeriesPageManager<ModalityPage>
	private previousCanvasSnapshot: { startDate: Date; endDate: Date; bitmap: ImageBitmap } | null = null
	private offscreenCanvas: OffscreenCanvas
	private offscreenCanvasContext: OffscreenCanvasRenderingContext2D | null
	private offscreenXScale: ScaleTime<any, any, any>
	private pageRectangle: PageRectangle = { x: 0, y: 0, width: 0, height: 0 }
	private renderStrategy: TraceRenderStrategy
	private renderCacheKey: string

	public dataObjectId: number

	constructor(root: SVGGElement, config: TraceConfig, pageManager: TimeSeriesPageManager<ModalityPage>, reactCallbacks: ModalityGraphGroupReactCallbacks) {
		super(root, config, "d3-trace", reactCallbacks)
		this.pageManager = pageManager

		const width = config.xScale.range()[1]
		const height = this.getHeight()
		this.offscreenCanvas = new OffscreenCanvas(width, height)
		this.offscreenXScale = config.xScale.copy().range([0, width])
		this.renderStrategy = this.getRenderStrategy()
		this.offscreenCanvasContext = this.offscreenCanvas.getContext("2d")

		this.renderStrategy
			.getOffscreenRenderer({ xScale: this.offscreenXScale, yScale: this.config.yScale })
			.context(this.offscreenCanvasContext)

		this.dataObjectId = config.renderStrategy === RenderStrategy.DELTA 
			? reactCallbacks.dataSourceMap.get(config.first.dataSource) ?? Infinity
			: reactCallbacks.dataSourceMap.get(config.dataSource) ?? Infinity

		this.renderCacheKey = this.getRenderCacheKey()

		this.updateDerivedState()
		this.render()
	}

	public renderPage = (page: ModalityPage | undefined) => {
		if (!page || !this.context || !this.offscreenCanvasContext || !(this.config.isVisible ?? true)) {
			return
		}

		const { x, y, width, height } = this.getPageRectangle(page.startTime, page.endTime)

		this.context.clearRect(x, y, width, height)

		// Check if there is a fresh cached render that is up-to-date
		const cachedTraceRender = page.renderCache.get(this.dataObjectId)?.get(this.renderCacheKey)

		if (cachedTraceRender && !cachedTraceRender.dirty) {
			const bitmap = cachedTraceRender.bitmap

			if (this.context.canvas.width === bitmap.width && this.context.canvas.height === bitmap.height) {
				this.context.drawImage(bitmap, x, y)
				return
			}
		}

		const unloadedRectangle = this.getUnloadedPageRectangle(page)

		// If there is data and no cache, use the data to redraw
		this.renderStrategy.renderPage(page, this.reactCallbacks, this.offscreenXScale, this.offscreenCanvas, this.context, this.offscreenCanvasContext, this.pageRectangle, unloadedRectangle)
		
	}

	private getUnloadedPageRectangle = (page: ModalityPage): PageRectangle | null => {
		if (!this.offscreenCanvasContext) {
			return null
		}

		let unloadedStartTime

		if (this.config.renderStrategy !== RenderStrategy.DELTA) {
			const buffer = page.responseBuffers.get(this.config.dataKey)
			unloadedStartTime = buffer?.unloadedStartTime
		} else {
			const first = page.responseBuffers.get(this.config.first.dataKey)?.unloadedStartTime ?? Infinity
			const second = page.responseBuffers.get(this.config.second.dataKey)?.unloadedStartTime ?? Infinity
			unloadedStartTime = Math.min(first, second)
		}

		if (unloadedStartTime === Infinity || unloadedStartTime === null || !unloadedStartTime) {
			unloadedStartTime = page.startTime
		}

		// The offscreen canvas uses the page start time as the origin, so, these x coordinates have to be relative to that 0 point.
		const x0 = this.config.xScale(page.startTime)
		const x1 = this.config.xScale(unloadedStartTime) - x0
		const x2 = this.config.xScale(page.endTime) - x0

		return { x: x1, y: 0, width: x2-x1, height: this.offscreenCanvas.height}
	}

	public isRescaling = () => this.previousCanvasSnapshot !== null

	public getDataObjectId = (trace: { dataSource: DataSource }) => {
		return this.reactCallbacks.dataSourceMap.get(trace.dataSource)
	}

	public rescale = () => {
		if (this.context && this.previousCanvasSnapshot) {
			this.pageManager.getAllLoadedPages().forEach(page => {
				page?.renderCache.get(this.dataObjectId)?.delete(this.getRenderCacheKey())
			})

			this.context.clearRect(0, 0, this.context.canvas.width, this.context.canvas.height)
			const { x, y, width, height } = this.getPageRectangle(this.previousCanvasSnapshot.startDate, this.previousCanvasSnapshot.endDate)
			this.context.drawImage(this.previousCanvasSnapshot.bitmap, x, y, width, height)
		}
	}

	public render = () => {
		if (this.previousCanvasSnapshot) {
			this.rescale()
			return
		}

		super.render()
	}

	public redraw = () => {
		this.pageManager.invalidateRenderCacheKey(this.renderCacheKey)

		// isVisible is possibly undefined, but we want to default to true if it's undefined
		if (!(this.config.isVisible ?? true)) {
			return
		}

		this.renderStrategy.render()
	}

	public takeSnapshot = async () => {
		if (this.context) {
			const bitmap = await createImageBitmap(this.context.canvas)
			const [startDate, endDate] = this.config.xScale.domain()
			this.previousCanvasSnapshot = { startDate, endDate, bitmap }
		}
	}

	public clearSnapshot = () => {
		this.previousCanvasSnapshot = null
	}

	protected getRenderCacheKey = () => {
		return this.renderStrategy.getRenderCacheKey()
	}

	public getPageRectangle = (startTime: Date | number, endTime: Date | number) => {
		const x1 = this.config.xScale(startTime)
		const x2 = this.config.xScale(endTime)
		const width = x2 - x1
		const height = this.getHeight()
		// rounding helps the render cache be extra fast but reduces quality
		this.pageRectangle.x = x1
		this.pageRectangle.width = width
		this.pageRectangle.height = height

		return this.pageRectangle
	}

	protected updateDerivedState = () => {
		let width = this.config.xScale.range()[1]
		let height = this.getHeight()

		if (width <= 0 || height <= 0) {
			return
		}

		this.offscreenCanvas.width = width
		this.offscreenCanvas.height = height

		this.pageManager.getAllLoadedPages().forEach(page => {
			page?.renderCache.get(this.dataObjectId)?.delete(this.getRenderCacheKey())
		})

		this.renderStrategy.updateConfig(this.config)
		this.renderCacheKey = this.getRenderCacheKey()
	}

	protected enter = (newTrace: Selection<EnterElement, any, any, any>): Selection<SVGForeignObjectElement, any, any, any> => {
		const foreignObject = newTrace.append("foreignObject").attr("class", this.className)
		const canvas = foreignObject.append("xhtml:canvas") as Selection<HTMLCanvasElement, any, any, any>

		// A transiently empty or reversed scale range would set negative canvas
		// dimensions, so floor at 1 px like the offscreen canvas does.
		const width = Math.max(1, this.config.xScale?.range()[1] - this.config.xScale?.range()[0])
		const height = Math.max(1, this.getHeight())
		const yOffset = Math.min(...this.config.yScale.range())

		foreignObject.attr("width", width).attr("height", height).attr("y", yOffset)
		canvas.attr("width", width).attr("height", height)

		const canvasNode = canvas.node()

		if (canvasNode != null) {
			this.context = canvasNode.getContext("2d") ?? undefined
			this.renderStrategy.getDirectRenderer({ xScale: this.config.xScale, yScale: this.config.yScale }).context(this.context)
		}

		if (this.config.isVisible ?? true) {
			this.renderStrategy.render()
		}

		return foreignObject
	}

	protected update = (updatedForeignObject: Selection<any, any, any, any>): Selection<any, any, any, any> => {
		const updatedCanvas = updatedForeignObject.select("canvas")

		const width = Math.max(1, this.config.xScale?.range()[1] - this.config.xScale?.range()[0])
		const height = Math.max(1, this.getHeight())
		const yOffset = Math.min(...this.config.yScale.range())

		updatedForeignObject.attr("width", width).attr("height", height).attr("y", yOffset)
		updatedCanvas.attr("width", width).attr("height", height)

		if (this.config.isVisible ?? true) {
			this.renderStrategy.render()
		}

		return updatedForeignObject
	}

	private getRenderStrategy(): TraceRenderStrategy {
		switch(this.config.renderStrategy) {
			case RenderStrategy.HEATMAP:
				return new HeatmapRenderStrategy(this.pageManager, this, this.config)
			case RenderStrategy.SPECTROGRAM:
				return new SpectrogramRenderStrategy(this.pageManager, this, this.config)
			case RenderStrategy.DELTA:
				return new DeltaRenderStrategy(this.pageManager, this, this.config)
			case RenderStrategy.POINT:
				return new PointRenderStrategy(this.pageManager, this, this.config as any) // TODO: why does this need "any"?
			case RenderStrategy.LINE_STEP:
				return new LineStepRenderStrategy(this.pageManager, this, this.config as any) // TODO: why does this need "any"?
			case RenderStrategy.INFUSION_MEDICATION:
				return new InfusionMedicationRenderStrategy(this.pageManager, this, this.config as any) // TODO: why does this need "any"?
			case RenderStrategy.INTERMITTENT_MEDICATION:
				return new IntermittentMedicationRenderStrategy(this.pageManager, this, this.config as any) // TODO: why does this need "any"?
			case RenderStrategy.LINE:
				return new LineRenderStrategy(this.pageManager, this, this.config as any) // TODO: why does this need "any"?
			default:
				return new LineRenderStrategy(this.pageManager, this, this.config as any) // TODO: why does this need "any"?

		}
	}

	private getHeight(): number {
		const [y1, y2] = this.config.yScale.range()
		return Math.abs(y1 - y2)
	}
}
